import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// 🚀 OPTIMIZATION: Date and time utilities
export function formatDateRange(startDate: string, endDate: string): string {
  try {
    if (!startDate || !endDate) return 'Date range unavailable'
    
    const start = new Date(startDate)
    const end = new Date(endDate)
    
    // Check if dates are valid
    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      return 'Invalid date range'
    }
    
    const options: Intl.DateTimeFormatOptions = { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    }
    
    return `${start.toLocaleDateString('en-US', options)} - ${end.toLocaleDateString('en-US', options)}`
  } catch (error) {
    console.warn('Error formatting date range:', error)
    return 'Date formatting error'
  }
}

export function calculateDaysActive(startDate: string, endDate: string): number {
  try {
    if (!startDate || !endDate) return 0
    
    const start = new Date(startDate)
    const end = new Date(endDate)
    
    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      return 0
    }
    
    return Math.max(0, Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)))
  } catch (error) {
    console.warn('Error calculating days active:', error)
    return 0
  }
}

// 🚀 OPTIMIZATION: Performance calculation utilities
export function calculateScore(messageCount: number, channelCount: number, daysActive: number): number {
  try {
    // Simple scoring algorithm based on activity
    const messageWeight = Math.min((messageCount || 0) / 50, 20) // Max 20 points
    const channelWeight = Math.min((channelCount || 0) * 5, 15) // Max 15 points
    const consistencyWeight = Math.min((daysActive || 0) / 10, 15) // Max 15 points
    const baseScore = 50 // Base score
    
    return Math.round(baseScore + messageWeight + channelWeight + consistencyWeight)
  } catch (error) {
    console.warn('Error calculating score:', error)
    return 50
  }
}

// 🚀 OPTIMIZATION: Array and data utilities
export function safeArrayAccess<T>(array: T[] | undefined | null, defaultValue: T[] = []): T[] {
  return Array.isArray(array) ? array : defaultValue
}

export function safeStringAccess(str: string | undefined | null, defaultValue: string = ''): string {
  return typeof str === 'string' ? str : defaultValue
}

export function safeNumberAccess(num: number | undefined | null, defaultValue: number = 0): number {
  return typeof num === 'number' && !isNaN(num) ? num : defaultValue
}

// 🚀 OPTIMIZATION: Performance measurement utilities
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null
  
  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout)
    timeout = setTimeout(() => func(...args), wait)
  }
}

export function throttle<T extends (...args: any[]) => any>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let lastFunc: NodeJS.Timeout | null = null
  let lastRan: number
  
  return (...args: Parameters<T>) => {
    if (!lastRan) {
      func(...args)
      lastRan = Date.now()
    } else {
      if (lastFunc) clearTimeout(lastFunc)
      lastFunc = setTimeout(() => {
        if ((Date.now() - lastRan) >= limit) {
          func(...args)
          lastRan = Date.now()
        }
      }, limit - (Date.now() - lastRan))
    }
  }
}

// 🚀 OPTIMIZATION: Local storage utilities with error handling
export function safeLocalStorageGet(key: string, defaultValue: string = ''): string {
  try {
    return localStorage.getItem(key) || defaultValue
  } catch (error) {
    console.warn('Error accessing localStorage:', error)
    return defaultValue
  }
}

export function safeLocalStorageSet(key: string, value: string): boolean {
  try {
    localStorage.setItem(key, value)
    return true
  } catch (error) {
    console.warn('Error setting localStorage:', error)
    return false
  }
} 